/**
 * LanguageSwitcher Component
 *
 * This component provides a button to switch the application's language
 * between English and Arabic. It handles both local and server-side
 * language updates, applies the correct text direction (LTR or RTL),
 * and reloads the page to ensure the language change is reflected
 * throughout the app.
 *
 * Features:
 * - Detects the current language using i18next.
 * - Switches language on button click.
 * - Persists the selected language in localStorage.
 * - Updates the document's text direction (dir attribute) for proper layout.
 * - If a user is authenticated, calls the backend API to update their
 *   preferred language using the `useChangeLanguage` hook.
 * - Displays any error returned from the language change API.
 *
 * Dependencies:
 * - `react-i18next` for internationalization.
 * - `react-redux` for accessing authentication state.
 * - `useChangeLanguage` custom hook for server-side language updates.
 * - Constants: `ArabicCode`, `EnglishCode`, `BASE_LOCAL_IMAGES_DIR`.
 * - Flag images: `english_flag_IMG`, `ksa_flag_IMG`.
 *
 * Usage:
 * Simply include <LanguageSwitcher /> anywhere in the application UI,
 * usually in the header or a top-level component.
 */

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useChangeLanguage } from "../../utils/api/General/useChangeLanguage";
import {
  ArabicCode,
  BASE_LOCAL_IMAGES_DIR,
  EnglishCode,
} from "../../utils/constants/CommonConstants";
import { english_flag_IMG, ksa_flag_IMG } from "../../utils/constants/Images";

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const { user, token } = useSelector((state: RootState) => state.auth);
  const { mutate: ChangeLanguageMutate, error: ChangeLanguageError } =
    useChangeLanguage();

  //?===============Change Language Handler================
  const handleChangeLanguage = (language: string) => {
    const currentToken = token || user?.token;
    const isArabic = language === ArabicCode;
    const dir = isArabic ? "rtl" : "ltr";

    const applyLanguageChange = () => {
      localStorage.setItem("language", language);
      i18n.changeLanguage(language);
      document.documentElement.setAttribute("dir", dir);
      window.location.reload();
    };

    if (language) {
      if (currentToken) {
        ChangeLanguageMutate(
          {
            payload: { Language: language },
            token: currentToken,
          },
          {
            onSuccess: (response) => {
              if (response) {
                applyLanguageChange();
              }
            },
          },
        );
      } else {
        applyLanguageChange();
      }
    }
  };

  return (
    <>
      {i18n.language.slice(0, 2) === EnglishCode ? (
        <button
          className="absolute -end-2 top-1 z-10 flex h-14 w-28 scale-[60%] items-center justify-center gap-2 rounded-xl bg-white p-2 px-4 py-2 sm:end-4 sm:top-3 sm:scale-100 md:end-14 md:top-12"
          onClick={() => handleChangeLanguage(ArabicCode)}
        >
          <div className="flex items-center gap-2">
            <img
              src={`${BASE_LOCAL_IMAGES_DIR}/${ksa_flag_IMG}`}
              alt="flag"
              loading="lazy"
              className="w-6 cursor-pointer"
            />
            <p className="font-ar text-headingTextColor">العربية</p>
          </div>
          {ChangeLanguageError ? ChangeLanguageError.message : null}
        </button>
      ) : (
        <button
          className="absolute end-5 top-5 z-10 flex h-14 w-28 items-center justify-center gap-2 rounded-xl bg-white p-2 px-4 py-2 lg:end-20 lg:top-12"
          onClick={() => handleChangeLanguage(EnglishCode)}
        >
          <div className="font-en flex items-center gap-2">
            <img
              src={`${BASE_LOCAL_IMAGES_DIR}/${english_flag_IMG}`}
              alt="flag"
              loading="lazy"
              className="w-6 cursor-pointer"
            />
            <p>English</p>
          </div>
          {ChangeLanguageError ? ChangeLanguageError.message : null}
        </button>
      )}
    </>
  );
};

export default LanguageSwitcher;
